import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import { useTheme } from '../theme/use-theme.js';

/**
 * Toasts, on sonner.
 *
 * A toast is for a result the person does not have to act on. Anything that needs a decision is a
 * dialog, and anything that must not be missed is inline: a toast can be dismissed before it is
 * read.
 */
export interface ToasterProps {
  position?: React.ComponentProps<typeof SonnerToaster>['position'];
  /** How long a toast stays, in milliseconds. Errors stay until dismissed. */
  duration?: number;
}

export function Toaster({ position = 'bottom-right', duration = 5000 }: ToasterProps) {
  const { resolved } = useTheme();
  return (
    <SonnerToaster
      theme={resolved}
      position={position}
      duration={duration}
      // Sonner's default shortcut for focusing the notifications is `Alt+T`, which is this
      // product's transfer key: an agent reaching for a transfer would have landed in the toast
      // region instead. `Alt+N` for notifications, and the product keeps its letter.
      hotkey={['altKey', 'KeyN']}
      // The live region must be polite: a toast interrupting an agent mid-call is worse than late.
      toastOptions={{
        classNames: {
          toast:
            'rounded-(--radius-lg) border border-(--color-border) bg-(--color-surface-elevated) text-(--color-text-primary) shadow-(--shadow-overlay)',
          description: 'text-(--color-text-secondary)',
          actionButton: 'bg-(--color-accent) text-(--color-accent-foreground)',
          cancelButton: 'bg-(--color-surface-sunken) text-(--color-text-secondary)',
          error: 'border-(--color-danger) text-(--color-danger)',
          success: 'border-(--color-success)',
          warning: 'border-(--color-warning)',
        },
      }}
    />
  );
}

/** The only way the product raises a toast, so the shape stays consistent. */
export const toast = sonnerToast;
